import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import clearForm from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/ClearForm";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Page from "@insite/mobius/Page";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomReportIssueForm from "@tCooneyBrothers/Components/CustomReportIssueForm";
import * as React from "react";
import { css } from "styled-components";

interface OwnProps extends PageProps { }

type Props = OwnProps & HasShellContext;

export interface UnhandledErrorModalStyles {
    secondary?: ButtonPresentationProps;
    tertiary?: ButtonPresentationProps;
    successModal?: ModalPresentationProps;
    modalContainer?: GridContainerProps;
    messageTextGridItem?: GridItemProps;
    cmsStyles?: InjectableCss;
    actionButtons?: InjectableCss;
    pageStyle?: InjectableCss;
}

export const unhandledErrorModalStyles: UnhandledErrorModalStyles = {
    secondary: {
        variant: "secondary",
    },
    tertiary: {
        variant: "tertiary",
        css: css`
            margin-right: 35px;
            width: 137px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin: 5px;
                        `,
                        css`
                            margin: 5px;
                        `,
                        null
                    ],
                    "max",
                )}
        `,
    },
    successModal: { sizeVariant: "small" },
    messageTextGridItem: {
        width: 12,
        css: css`
            padding-top: 0;
            padding-bottom: 0;
        `,
    },
    cmsStyles: {
        css: css`
            h2 {
                line-height: 38px;
                margin-top: 8px;
            }

            p {
                margin-bottom: 20px !important;
                font-size: 15px;
            }
        `,
    },
    actionButtons: {
        css: css`
            text-align: center;
        `,
    },
    pageStyle: {
        css: css`
            padding-bottom: 0 !important;
            padding-top: 0 !important;
        `,
    }
}

const styles = unhandledErrorModalStyles;

const UnhandledErrorModal = ({
    id,
    fields,
    shellContext: { isCurrentPage, isInShell },
}: Props) => {

    const [isReportIssuePopUpEnable, setIsReportIssuePopUpEnable] = React.useState(false);

    const relaodPage = () => {
        window.location.reload();
        window.scrollTo(0, 0);
    };

    const ReportIssueModalCloseHandler = () => {
        setIsReportIssuePopUpEnable(false);
    };

    const ReportIssueModalOpenHandler = () => {
        setIsReportIssuePopUpEnable(true);
    };

    const continueButtonClickHandlerToParent = (modalState: boolean) => {
        modalState = false;
        setIsReportIssuePopUpEnable(modalState);
        clearForm();
    };

    return (
        <Page {...styles.pageStyle}>
            {isCurrentPage && isInShell && (
                // we can't use the PageTitle widget because it will always show the title of the current page. That would work in the shell, but not when the modal displays in the real world
                <Typography variant="h2" as="h1">
                    {fields["modalTitle"]}
                </Typography>
            )}
            <StyledWrapper {...styles.cmsStyles}>
                <Zone contentId={id} zoneName="Content" />
            </StyledWrapper>
            <StyledWrapper {...styles.actionButtons}>
                <Button {...styles.tertiary} onClick={relaodPage}>
                    {translate("Try Again")}
                </Button>
                <Button {...styles.secondary} onClick={ReportIssueModalOpenHandler}>
                    {translate("Report Issue")}
                </Button>
            </StyledWrapper>

            <Modal
                headline={siteMessage("ReportAnIssueHeading")}
                contentLabel="Report an Issue"
                {...styles.successModal}
                isOpen={isReportIssuePopUpEnable}
                handleClose={ReportIssueModalCloseHandler}
            >
                <GridContainer {...styles.modalContainer}>
                    <GridItem {...styles.messageTextGridItem}>
                        <CustomReportIssueForm 
                            parentModalState={continueButtonClickHandlerToParent}
                        />
                    </GridItem>
                </GridContainer>

            </Modal>
        </Page>
    );
}

const pageModule: PageModule = {
    component: withIsInShell(UnhandledErrorModal),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
        fieldDefinitions: [
            {
                name: "modalTitle",
                displayName: "Modal Title",
                editorTemplate: "TextField",
                defaultValue: "Unhandled Error",
                fieldType: "Translatable",
                isRequired: true,
                sortOrder: 0,
            },
        ],
    },
};

export default pageModule;
